import axiosInstance from "./axios";
import { validateWithSchema } from "./zodUtils";
import { ExcelUploadResponse } from "./generated/schemas";
import {
  extractArrayBufferErrorJson,
  extractArrayBufferErrorMessage,
  extractHeaderStringArray,
} from "./apiErrorUtils";

const apiUrl = import.meta.env.VITE_BACKEND_API_URL;

export const fetchExcelTemplate = async (bearerToken) => {
  try {
    const response = await axiosInstance.get(`${apiUrl}/excel/template/100v1`, {
      headers: {
        Authorization: `Bearer ${bearerToken}`,
      },
      responseType: "arraybuffer", // Ensure the response is treated as binary data
    });
    return response.data; // Return only the raw data
  } catch (error) {
    console.error("Failed to fetch Excel template:", error);
    throw error;
  }
};

export const fetchExcelProducts = async (
  bearerToken: string,
  companyId: number,
  organizationId?: number
) => {
  try {
    const url = organizationId
      ? `${apiUrl}/excel/export/${companyId}/products?organizationId=${organizationId}`
      : `${apiUrl}/excel/export/${companyId}/products`;

    const response = await axiosInstance.get(url, {
      headers: {
        Authorization: `Bearer ${bearerToken}`,
      },
      responseType: "arraybuffer", // Ensure the response is treated as binary data
    });
    return new Uint8Array(response.data); // Return as Uint8Array
  } catch (error) {
    console.error("Failed to fetch Excel products:", error);
    throw error;
  }
};

export const postExcelTemplate = async (
  file: File,
  bearerToken: string,
  companyId: number,
  organizationId?: number,
  existingReportId?: number,
  year?: number,
  quarter?: number,
  month?: number
): Promise<
  | {
    responseType: "excel";
    data: ArrayBuffer;
    warnings?: string[];
    errors?: string[];
  }
  | { responseType: "json"; data: ExcelUploadResponse }
  | void
> => {
  try {
    const formData = new FormData();
    formData.append("file", file);

    const url = organizationId
      ? `${apiUrl}/excel/${companyId}?organizationId=${organizationId}&existingReport=${existingReportId || 0}&year=${year || 0}&quarter=${quarter || 0}`
      : `${apiUrl}/excel/${companyId}?existingReport=${existingReportId || 0}&year=${year || 0}&quarter=${quarter || 0}`;

    const response = await axiosInstance.post(url, formData, {
      headers: {
        Authorization: `Bearer ${bearerToken}`,
      },
      responseType: "arraybuffer", // Ensure binary data is handled correctly
    });

    const contentType = response.headers["content-type"];
    if (contentType.includes("application/json")) {
      // Parse JSON response
      const text = new TextDecoder().decode(response.data);
      return {
        responseType: "json",
        data: validateWithSchema(ExcelUploadResponse, JSON.parse(text)),
      };
    } else if (
      contentType.includes(
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
      )
    ) {
      // Return binary data for Excel file, along with any warnings/errors
      // reported via headers since the body can't carry JSON here.
      return {
        responseType: "excel",
        data: response.data,
        warnings: extractHeaderStringArray(response.headers, "x-sumi-warnings"),
        errors: extractHeaderStringArray(response.headers, "x-sumi-errors"),
      };
    }
  } catch (error) {
    console.error("Failed to upload Excel template:", error);

    // Some validation failures come back with a non-2xx status but still
    // carry a full ExcelUploadResponse body; treat those as a normal summary
    // instead of throwing a generic error.
    const errorJson = extractArrayBufferErrorJson(error);
    if (errorJson) {
      return {
        responseType: "json",
        data: validateWithSchema(ExcelUploadResponse, errorJson),
      };
    }

    const message = extractArrayBufferErrorMessage(error);
    throw message ? new Error(message) : error;
  }
};
